import qs from 'qs';
import HOSTS from 'tradera-constants/hosts';
import initData from 'init-data';
import { logger } from 'packages/logger';
import { URL_LANGUAGES } from 'tradera-lang/constants.mjs';

// List of SPA paths for the regex match, without initial slash
const SPA_REGEX_PATHS = [
    'search',
    'campaign',
    'charity',
    'kategorier',
    'profile\\/items',
    'brand\\/.+',
    // Category
    '[a-z]+[\\w\\-]+[\\-|_]\\d+'
];
const SPA_PATH_REGEX = new RegExp(
    `^(\\/en|\\/da|\\/de)?/(${SPA_REGEX_PATHS.join('|')})`
);

/**
 * Returns language parameter from url
 * @param {*} url
 */
const getLanguagePrefixFromUrl = url => {
    const matches = url.match(/\/([a-z][a-z])((\/.*$)|$)/);

    if (matches === null) {
        return null;
    }

    const firstMatch = matches.find((match, index) => index > 0 && match);

    if (URL_LANGUAGES.indexOf(firstMatch) !== -1) {
        return firstMatch;
    }

    return null;
};

/**
 * Format query params parsed from query string
 *
 * @param {object} queryParams
 * @returns {object}
 */
const formatQueryParams = queryParams => {
    const { categoryId, ...rest } = queryParams;
    const formattedQueryParams = rest;
    if (categoryId !== undefined) {
        formattedQueryParams.categoryId = parseInt(categoryId, 10);
    }
    return formattedQueryParams;
};

/**
 * Check if url is absolute
 *
 * @param {string} url
 * @returns {boolean}
 */
const isAbsoluteUrl = url => new RegExp('^(?:[a-z]+:)?//', 'i').test(url);

/**
 * Tests if a given url or path is an SPA link or not
 *
 * @param {string} urlOrPath
 * @returns {boolean}
 */
function isSpaLink(urlOrPath) {
    let pathname;
    if (/(^http)s?:\/\//i.test(urlOrPath)) {
        try {
            pathname = new URL(urlOrPath).pathname;
        } catch {
            return false;
        }
    } else {
        pathname = urlOrPath;
    }
    return SPA_PATH_REGEX.test(pathname);
}

/**
 * Takes an object and returns a new object without attribute properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripAttributesFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    const { attributes, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an object and returns a new object without pagination properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripCategoryIdFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    const { categoryId, ...rest } = queryParams;
    return rest;
};

/**
 * Removes tradera base from a url, leaving path and query
 *
 * @param {string} url
 * @returns {string}
 */
const stripHost = url =>
    url.startsWith('http') ? url.replace(HOSTS.TRADERA, '') : url;

/**
 * Takes an object and returns a new object without itm properties
 * Itm tags should no longer be used, so this method will then be obsolete
 * @param {object} queryParams
 * @returns {object}
 */
const stripItmFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    const { itm_source, itm_medium, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an object and returns a new object without pagination properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripPaginationFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    const { spage, paging, page, fe, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an object and returns a new object without savedQueryName properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripSavedQueryNameFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    const { savedQueryName, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an url and adds tradera base url if it is not already an absolute url
 *
 * @param {string} relativeUrl
 * @returns {string}
 */
const toAbsoluteUrl = relativeUrl =>
    relativeUrl.startsWith('http')
        ? relativeUrl
        : `${HOSTS.TRADERA}${
              relativeUrl.startsWith('/') ? relativeUrl : '/' + relativeUrl
          }`;

/**
 * From react router location object to absolute url string
 * @param {{pathname: string, search: string}} url
 * @returns {string}
 */
const toAbsoluteUrlFromObject = url =>
    toAbsoluteUrl(
        `${url.pathname ? url.pathname : ''}${url.search ? url.search : ''}`
    );

/**
 * From URL to URL with language prefix
 * @param {*} url
 * @param {*} languageCodeIso2
 */
const toLocalizedUrl = (url, languageCodeIso2) => {
    if (url === null || url === undefined || url === '' || url === false) {
        return url;
    }

    const ignoredUrls = [initData.webApiUrl, '/api/webapi/'];
    const isIgnoredUrl = ignoredUrls.find(domain => url.indexOf(domain) !== -1);
    if (isIgnoredUrl) {
        return url;
    }

    if (!languageCodeIso2) {
        logger(`Missing language code for URL ${url}`);
        return url;
    }

    const isAbsolute = isAbsoluteUrl(url);

    const urlObject = isAbsolute
        ? new URL(url)
        : new URL(url, 'http://dummy.com');
    const origin = isAbsolute ? urlObject.origin : '';
    const localisedPathname = setLanguagePrefix(
        urlObject.pathname,
        languageCodeIso2
    );

    return `${origin}${localisedPathname}${urlObject.search}`;
};

/**
 * Creates query parameters object from search string
 * @param {string} search
 */
const toQueryParameters = search =>
    qs.parse(search, { ignoreQueryPrefix: true });

/**
 * Removes the Tradera host from a url, making it relative
 * @param {string} url
 * @returns {string}
 */
const toRelativeTraderaUrl = url => {
    if (url.startsWith(HOSTS.TRADERA)) {
        return url.replace(HOSTS.TRADERA, '');
    } else {
        return url;
    }
};

/**
 * Creates search string from all properties in object
 * @param {object} queryParams
 * @param {{[addQueryPrefix]: bool, [encode]: bool, [skipNulls]: bool }}
 * @returns {string}
 */
const toSearchString = (
    queryParams,
    {
        addQueryPrefix = true,
        encode = true,
        skipNulls = true,
        arrayFormat = 'repeat' //qs.stringify({ a: ['b', 'c'] }, { arrayFormat: 'repeat' }) ==> 'a=b&a=c'
    } = {}
) =>
    qs.stringify(queryParams, {
        addQueryPrefix: addQueryPrefix,
        encode: encode,
        skipNulls: skipNulls,
        arrayFormat: arrayFormat
    });

const setLanguagePrefix = (path, languageCodeIso2) => {
    const languagePrefix = getLanguagePrefixFromUrl(path);
    const defaultLanguage = 'sv';

    if (languagePrefix) {
        if (languageCodeIso2 === defaultLanguage) {
            return path.replace(`/${languagePrefix}`, '');
        } else {
            return path.replace(languagePrefix, languageCodeIso2);
        }
    } else {
        if (languageCodeIso2 === defaultLanguage) {
            return path;
        } else {
            return `/${languageCodeIso2}${path}`;
        }
    }
};

const getBackToHereRedirectUrl = () => {
    // Add random query parameter to force reload when redirecting to this url
    // and only the hash differs
    const queryParams = {
        ...qs.parse(location.search, { ignoreQueryPrefix: true }),
        forceRedirect: Math.round(Math.random() * 100000)
    };
    return (
        location.protocol +
        '//' +
        location.host +
        location.pathname +
        qs.stringify(queryParams, { addQueryPrefix: true })
    );
};

export {
    getLanguagePrefixFromUrl,
    formatQueryParams,
    getBackToHereRedirectUrl,
    isAbsoluteUrl,
    isSpaLink,
    stripAttributesFromQueryParameters,
    stripCategoryIdFromQueryParameters,
    stripHost,
    stripItmFromQueryParameters,
    stripPaginationFromQueryParameters,
    stripSavedQueryNameFromQueryParameters,
    toAbsoluteUrl,
    toAbsoluteUrlFromObject,
    toLocalizedUrl,
    toQueryParameters,
    toRelativeTraderaUrl,
    toSearchString
};
